import axios from "axios";
import * as Howler from "howler";
import * as Logger from "js-logger";
import { FatalErrorCommand } from "slotworks/commands/error/fatal-error-command";
import Components from "slotworks/components/components";
import { AssetConfigSchema, FileLocation, ManifestSchema } from "slotworks/config/asset-config-schema";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import DeviceInfo from "slotworks/utils/device-info";
import * as WebFont from "webfontloader";

declare const __VERSION__: string;

class Loader {

    /**
     * Keep a record of which files belong to which stage, so we can later unload and reload them
     */
    private stages: FileLocation[][];

    /**
     * Keeps a record of which specific textures belong to which stage
     */
    private stageIds: string[][] = [];

    /**
     * Keeps a record of which stages have been loaded
     */
    private loadedStages: Map<number, boolean> = new Map<number, boolean>();

    /**
     * Cached asset config schema for subsequent loads after main load
     */
    private assetConfig: AssetConfigSchema;

    /**
     * Merged manifest of layout specific and general assets
     */
    private mergedManifest: ManifestSchema;

    /**
     * Loads the first three stages (the preloader itself is stage 0, then game stage 1 and 2)
     * and displays load progress with a graphical load bar
     *
     * @param manifest {ManifestSchema}
     * @param onLoadProgress {(stage:number, progress:number, resource:PIXI.loaders.Resource)=>{}}
     * @return {Contract<void>}
     */
    public load(config: AssetConfigSchema, onLoadProgress?: (stage: number, progress: number, resource: PIXI.loaders.Resource) => void): Contract<void> {

        this.assetConfig = config;

        DeviceInfo.layoutsToLoad;

        const deviceManifest = config.layouts[DeviceInfo.layoutId].manifest;

        const deviceGraphicsManifest = deviceManifest.graphics;
        
        // this.stages = deviceGraphicsManifest.x3;
        this.stages = [ [], []]; // do i need 3

        let domElements: string[];
        // animations
        let allAtlasses;

        // todo rewrite
        if(CanvasManager.resolution < 2) {
            for(let layout of DeviceInfo.layoutsToLoad) {
                const graphics = config.layouts[layout].manifest.graphics.x1;
                for(let stageId in graphics) {
                    this.stages[stageId].push(...graphics[stageId]);
                }
            }
            domElements = deviceManifest.DOMElements.x1;
            allAtlasses = config.layouts.all.manifest.graphics.x1;
        } else if(CanvasManager.resolution < 3) {
            for(let layout of DeviceInfo.layoutsToLoad) {
                const graphics = config.layouts[layout].manifest.graphics.x2;
                for(let stageId in graphics) {
                    this.stages[stageId].push(...graphics[stageId]);                   
                }
            }
            domElements = deviceManifest.DOMElements.x2;
            allAtlasses = config.layouts.all.manifest.graphics.x2;
        } else {
            for(let layout of DeviceInfo.layoutsToLoad) {
                const graphics = config.layouts[layout].manifest.graphics.x3;
                for(let stageId in graphics) {
                    this.stages[stageId].push(...graphics[stageId]);
                }
            }
            domElements = deviceManifest.DOMElements.x3;
            allAtlasses = config.layouts.all.manifest.graphics.x3;
        }

        if (config.layouts.all) {
            this.stages[1] = this.stages[1].concat(config.layouts.all.manifest.files, allAtlasses[1]);
        }
        this.mergedManifest = config.layouts.all.manifest;

        if (this.stages.length < 3) {
            this.stages[2] = [];
        }

        this.stages.forEach((value, index, array) => {
            if (value === null) {
                array[index] = [];
            }
        });

        // Update graphical preloader when a file is loaded
        const preloaderUpdate = (stage: number, progress: number, resource: PIXI.loaders.Resource) => {
            Components.preloader.fileLoaded(resource);
            if (onLoadProgress) {
                onLoadProgress(stage, progress, resource);
            }
        };

        // Load stage 0 (the preloader), then stages 1 and 2
        return new Contract<void>((resolve) => {
            this.loadFonts(this.mergedManifest.fonts).then(() => {
                this.loadStage(0, onLoadProgress)
                    .then(() => {
                        Components.preloader.init(this.stages[1].concat(this.stages[2]));
                        this.loadDOMElements(domElements).then(() => {
                            this.loadStage(1, preloaderUpdate).then(() => {
                                this.loadStage(2, preloaderUpdate).then(() => {
                                    Components.preloader.loadComplete().then(() => {
                                        resolve(null);
                                    });
                                });
                            });
                        });
                    });
            });
        });
    }

    /**
     * Unloads a stage from memory
     *
     * @param stage {number}
     */
    public unloadStage(stage: number) {
        if (this.stageIds[stage]) {
            while (this.stageIds[stage].length) {
                const textureId = this.stageIds[stage].pop();
                GraphicsManager.destroyTexture(textureId);
            }

            this.loadedStages.delete(stage);

            this.log("Unloaded stage " + stage.toString());
        } else {
            this.log("Could not unload stage. Stage " + stage.toString() + " is not loaded");
        }
    }

    /**
     * Loads a specified stage
     * @param stage {number}
     * @param onLoadProgress {(stage:number, progress:number, resource:PIXI.loaders.Resource)=>{}}
     * @return {Contract<void>}
     */
    public loadStage(stage: number, onLoadProgress?: (stage: number, progress: number, resource: PIXI.loaders.Resource) => void): Contract<void> {

        return new Contract<void>((resolve) => {

            if (this.loadedStages.has(stage)) {
                resolve(null);
            } else {

                const filesToLoad: FileLocation[] = this.stages[stage];
                this.log("Loading images stage " + stage.toString());                

                if (filesToLoad.length === 0) {
                    resolve(null);
                    return;
                }

                PIXI.loader.pre((resource: PIXI.loaders.Resource, next: Function) => {
                    if (resource.url.indexOf("?") === -1) {
                        resource.url += "?" + __VERSION__;
                    }
                    next();
                });
                
                const failed: FileLocation[] = [];

                let attempt: number = 0;
                const maxAttempts = 2;

                const done = () => {
                    this.log("Stage " + stage.toString() + " load complete");
                    this.loadedStages.set(stage, true);
                    resolve(null);
                }

                const load = (files:FileLocation[]) => {
                    if(files.length == 0) {
                        done();
                        return;
                    }
                    failed.length = 0;
                    PIXI.loader.reset();
                    PIXI.loader.removeAllListeners();
                    attempt++;
                    
                    PIXI.loader
                    .add(files)
                    .on("progress", (loader: PIXI.loaders.Loader, resource: PIXI.loaders.Resource) => {
                        if (resource.error) {
                            this.log("Failed to load " + resource.url);
                            const found = files.find(fileLocation => 
                                resource.name == fileLocation.name || resource.name == fileLocation.name + "_image"
                            );
                            if(found) {
                                if(failed.indexOf(found) == -1)
                                    failed.push(found);
                            }
                        } else {
                            this.fileLoaded(stage, resource);
                            if (onLoadProgress) {
                                onLoadProgress(stage, loader.progress, resource);
                            }                           
                        }
                    })
                    .load(() => {
                        if(failed.length == 0) {
                            done();
                        } else if(attempt < maxAttempts) {
                            setTimeout(() => load(failed.slice()), 100);
                        } else {
                            FatalErrorCommand("err.ref");
                            Logger.error(failed);
                        }
                    });
                } 
                load(filesToLoad);
            }
        });
    }

    public loadSounds(): Contract<void> {
        SoundManager.setLoading();

        const manifest = this.mergedManifest;

        return new Contract<void>((resolve) => {
            const soundConfig = manifest.sound;

            const sound = new Howler.Howl({
                src: soundConfig.src + "?" + __VERSION__,
                sprite: soundConfig.sprite as IHowlSoundSpriteDefinition
            } as IHowlProperties);

            sound.once("load", () => {
                this.soundLoaded(sound);
                resolve(null);
            });
        });
    }

    public loadI18n(langCode: string): Contract<any> {
        return new Contract<any>((resolve) => {
            axios.get("i18n/" + langCode + ".json?" + __VERSION__).then((response) => {
                this.log("i18n loaded", response.data);
                resolve(response.data);
            }).catch(() => {
                if (langCode === "en") {
                    FatalErrorCommand("Translations failed to load");
                } else {
                    this.loadI18n("en").then((data) => {
                        resolve(data);
                    });
                }
            });
        });
    }

    public getStageFiles(stage: number): FileLocation[] {
        return this.stages[stage];
    }

    private loadFonts(fonts: string[]): Contract<void> {
        return new Contract<void>((resolve) => {
            if (!fonts) {
                Logger.warn("No fonts to load");
                resolve(null);
            } else {
                const config = {
                    custom: {
                        families: fonts,
                        urls: ["font/fonts.css?" + __VERSION__]
                    },
                    active: () => {
                        resolve(null);
                    },
                    fontinactive: (familyName, fvd) => {
                        Logger.warn("Invalid font: " + familyName);
                    },
                    inactive: () => {
                        Logger.warn("Invalid fonts");
                        resolve(null);
                    }
                } as WebFont.Config;

                WebFont.load(config);
            }
        });
    }

    private loadDOMElements(elements: string[]) {
        if (!elements || !elements.length) {
            return Contract.empty();
        }

        const domPreloadElement = document.createElement("div");
        domPreloadElement.id = "DOMPreloadElement";
        domPreloadElement.style.position = "absolute";
        domPreloadElement.style.width = "0";
        domPreloadElement.style.height = "0";
        domPreloadElement.style.overflow = "hidden";
        domPreloadElement.style.zIndex = "-1";

        document.body.appendChild(domPreloadElement);

        let contentString = "";

        return new Contract<void>((resolve) => {
            let imagesToLoad = elements.length;
            const onImageLoaded = () => {
                imagesToLoad--;

                if (imagesToLoad <= 0) {
                    domPreloadElement.style.content = contentString;

                    resolve(null);
                }
            };

            for (const element of elements) {
                const cacheBustedPath = element + "?" + __VERSION__;
                contentString += "url(" + cacheBustedPath + ") ";

                const img = new Image();
                img.src = cacheBustedPath;
                if (img.complete) {
                    onImageLoaded();
                } else {
                    img.onload = onImageLoaded;
                }
            }
        });
    }

    private soundLoaded(sound: Howl) {
        SoundManager.setSoundSprite(sound);
    }

    /**
     * When an asset is loaded, add it to the GraphicsManager so that it can be accessed by the game
     *
     * @param stage {number}
     * @param resource {PIXI.loaders.Resource}
     */
    private fileLoaded(stage: number, resource: PIXI.loaders.Resource) {
        let textureId = resource.name;

        if (!this.stageIds[stage]) {
            this.stageIds[stage] = [];
        }

        if (resource.textures) {
            for (const id of Object.keys(resource.textures)) {
                textureId = id;

                GraphicsManager.addTexture(textureId, resource.textures[id]);
                this.stageIds[stage].push(textureId);
            }
        } else if (resource.texture) {
            GraphicsManager.addTexture(textureId, resource.texture);
            this.stageIds[stage].push(textureId);
        }

    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string, obj: any = "") {
        Logger.info("%c Loader ", "background: #95f; color: #fff", message, obj);
    }
}

export default new Loader();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/loader/loader.ts